import React, { useState } from "react";
import { motion } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import { easing } from "@/lib/animations";

const fieldLabel = {
    fontFamily: "JetBrains Mono, monospace",
    fontSize: 10,
    letterSpacing: "0.28em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)",
};

const fieldStyle = {
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.25)",
    color: "#fff",
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: 14,
    padding: "12px 14px",
    letterSpacing: "0.02em",
    outline: "none",
    transition: "border-color 0.3s",
};

const CHANNELS = [
    { title: "Studio", body: "Atelier visits by appointment only.\nUlica Próżna 14/4 · 00-107 Warsaw · Poland" },
    { title: "Press", body: "press@proof.studio\nLookbook & samples available on request." },
    { title: "Stockists", body: "wholesale@proof.studio\nLine-sheet on demand for select retailers." },
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "general",
        message: "",
    });
    const [submitted, setSubmitted] = useState(false);

    const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = (e) => {
        e.preventDefault();
        if (!form.email || !form.message) return;
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setForm({ name: "", email: "", subject: "general", message: "" });
        }, 5000);
    };

    return (
        <PageShell
            sectionNumber="08"
            eyebrow="Open channel"
            title="Contact"
            crumb="Contact"
            data-testid="page-contact"
        >
            <div className="proof-grid-main">
                {/* FORM */}
                {submitted ? (
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: easing.primary }}
                        data-testid="contact-success"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 18,
                            padding: "32px 28px",
                            border: "1px solid rgba(255,255,255,0.18)",
                            background: "rgba(255,255,255,0.04)",
                        }}
                    >
                        <span
                            style={{
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                color: "#c1121f",
                            }}
                        >
                            Signal received
                        </span>
                        <p
                            style={{
                                margin: 0,
                                fontFamily: "'EB Garamond', serif",
                                fontStyle: "italic",
                                fontSize: 21,
                                lineHeight: 1.5,
                                color: "rgba(255,255,255,0.85)",
                                maxWidth: "44ch",
                            }}
                        >
                            We answer in lowercase, within two business days.
                            Watch your inbox.
                        </p>
                    </motion.div>
                ) : (
                    <form
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: 18,
                        }}
                    >
                        <label
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                        >
                            <span style={fieldLabel}>Name</span>
                            <input
                                type="text"
                                name="name"
                                value={form.name}
                                onChange={update}
                                required
                                data-testid="contact-name"
                                style={fieldStyle}
                            />
                        </label>
                        <label
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                        >
                            <span style={fieldLabel}>Email</span>
                            <input
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={update}
                                required
                                data-testid="contact-email"
                                style={fieldStyle}
                            />
                        </label>
                        <label
                            style={{
                                gridColumn: "1 / -1",
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                        >
                            <span style={fieldLabel}>Subject</span>
                            <select
                                name="subject"
                                value={form.subject}
                                onChange={update}
                                data-testid="contact-subject"
                                style={{ ...fieldStyle, cursor: "pointer" }}
                            >
                                <option value="general" style={{ background: "#0c0c0c" }}>
                                    General
                                </option>
                                <option value="press" style={{ background: "#0c0c0c" }}>
                                    Press / Editorial
                                </option>
                                <option value="wholesale" style={{ background: "#0c0c0c" }}>
                                    Wholesale
                                </option>
                                <option value="returns" style={{ background: "#0c0c0c" }}>
                                    Returns / Order issue
                                </option>
                            </select>
                        </label>
                        <label
                            style={{
                                gridColumn: "1 / -1",
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                        >
                            <span style={fieldLabel}>Message</span>
                            <textarea
                                name="message"
                                value={form.message}
                                onChange={update}
                                required
                                rows={6}
                                data-testid="contact-message"
                                style={{ ...fieldStyle, resize: "vertical", minHeight: 140 }}
                            />
                        </label>

                        <button
                            type="submit"
                            data-testid="contact-submit"
                            style={{
                                gridColumn: "1 / -1",
                                background: "#fff",
                                color: "#0c0c0c",
                                border: "none",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                padding: "16px 0",
                                cursor: "pointer",
                                marginTop: 6,
                            }}
                        >
                            Transmit Message →
                        </button>
                    </form>
                )}

                {/* SIDE CHANNELS */}
                <aside
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 22,
                    }}
                    data-testid="contact-channels"
                >
                    {CHANNELS.map((c, i) => (
                        <motion.div
                            key={c.title}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{
                                duration: 0.7,
                                delay: 0.06 * i,
                                ease: easing.primary,
                            }}
                            style={{
                                padding: "22px 22px",
                                border: "1px solid rgba(255,255,255,0.14)",
                                background: "rgba(255,255,255,0.03)",
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                            }}
                        >
                            <span style={fieldLabel}>{c.title}</span>
                            <span
                                style={{
                                    fontFamily: "'Space Grotesk', sans-serif",
                                    fontSize: 15,
                                    color: "rgba(255,255,255,0.85)",
                                    lineHeight: 1.6,
                                    whiteSpace: "pre-line",
                                }}
                            >
                                {c.body}
                            </span>
                        </motion.div>
                    ))}
                </aside>
            </div>
        </PageShell>
    );
}
